<template>
  <ion-header>
    <ion-toolbar>
      <ion-buttons slot="start">
        <ion-button aria-label="Retour" @click="closePopover(null)">
          <ion-icon slot="icon-only" :icon="arrowBackCircleOutline" />
        </ion-button>
      </ion-buttons>

      <ion-title>
        Entrez les informations
        <div class="subtitle" v-if="roundName">{{ roundName }}</div>
      </ion-title>

      <ion-button
        v-if="initialData"
        slot="end"
        class="ion-padding-end"
        color="danger"
        fill="solid"
        size="small"
        :disabled="isReadOnly"
        @click="confirmDelete"
      >
        Supprimer
      </ion-button>
    </ion-toolbar>
  </ion-header>

  <ion-content class="ion-padding">
    <ion-label color="medium">
      Le remplissage des informations du tour n'est pas obligatoire : vous
      pouvez directement poursuivre la rédaction de la lettre de voiture.
    </ion-label>

    <div v-if="materials && materials.length > 1">
      <ion-card class="ion-no-margin ion-margin-bottom card-danger">
        <ion-card-header>
          <ion-card-title>Attention</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          Plusieurs matériaux sont sélectionnés pour ce tour. Merci de n'en
          garder qu'un.
        </ion-card-content>
      </ion-card>
    </div>

    <ion-list class="ion-margin-top">
      <ion-row>
        <ion-col>
          <div class="align-line-icon-badge">
            <ion-img
              class="width-icon-img-30"
              src="/assets/icon/material.svg"
            ></ion-img>

            <div class="align-chip-form ml-10">
              <ion-chip
                color="tertiary"
                outline="true"
                v-for="(material, index) in materials"
                :key="'material' + index"
                :disabled="isReadOnly"
              >
                <ion-label>{{ material }}</ion-label>
                <ion-icon
                  @click="deleteMaterial(material)"
                  :icon="closeCircle"
                ></ion-icon>
              </ion-chip>

              <ion-icon
                class="width-icon-img-30 ml-5 color-yellow-dark"
                :class="{ opacity4: !canAddMaterial }"
                :icon="addCircle"
                @click="addMaterial()"
              ></ion-icon>
            </div>
          </div>
        </ion-col>
      </ion-row>

      <ion-item class="ion-margin-top">
        <ion-label position="stacked">Poids (Tonnes)</ion-label>

        <ion-input
          name="tons"
          type="text"
          inputmode="decimal"
          v-model="tons"
          placeholder="0.00"
          :disabled="isReadOnly"
        ></ion-input>
      </ion-item>

      <template v-if="onSite">
        <ion-item class="ion-margin-top">
          <ion-label position="stacked">Chargement</ion-label>

          <ion-input
            name="source_worksite"
            type="text"
            v-model="source_worksite"
            maxlength="255"
            placeholder="Nom et/ou lieu"
            :disabled="isReadOnly"
          ></ion-input>
        </ion-item>

        <ion-item class="ion-margin-top">
          <ion-label position="stacked">Déchargement</ion-label>

          <ion-input
            name="target_worksite"
            type="text"
            v-model="target_worksite"
            maxlength="255"
            placeholder="Nom et/ou lieu"
            :disabled="isReadOnly"
          ></ion-input>
        </ion-item>
      </template>

      <ion-item class="ion-margin-top">
        <ion-label position="stacked">N° de bon</ion-label>

        <ion-input
          name="reference"
          type="text"
          v-model="reference"
          placeholder="Référence du bon"
          :disabled="isReadOnly"
        ></ion-input>
      </ion-item>
    </ion-list>
  </ion-content>

  <ion-footer>
    <ion-toolbar>
      <ion-grid>
        <ion-row>
          <ion-col size="4">
            <ion-button
              expand="block"
              fill="solid"
              color="light"
              @click="closePopover(null)"
            >
              Passer
            </ion-button>
          </ion-col>

          <ion-col size="8">
            <ion-button
              expand="block"
              color="primary"
              fill="solid"
              :disabled="disableValidation"
              @click="validate"
            >
              Valider
            </ion-button>
          </ion-col>
        </ion-row>
      </ion-grid>
    </ion-toolbar>
  </ion-footer>
</template>

<script>
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonFooter,
  IonImg,
  IonChip,
  IonIcon,
  modalController,
  alertController,
} from '@ionic/vue';
import { addCircle, arrowBackCircleOutline, closeCircle } from 'ionicons/icons';
import PopoverAddMaterial from '@/components/driver/popover/PopoverAddMaterial';
import { sendErrorToast } from '@/services/utils/toastUtils';

export default {
  name: 'PopoverAddRound',

  components: {
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonList,
    IonItem,
    IonLabel,
    IonInput,
    IonFooter,
    IonImg,
    IonChip,
    IonIcon,
  },

  props: [
    'closePopover',
    'defaultMaterials', // default coupon materials list
    'materialSuggestions',
    'oldRentalCouponSending',
    'disabled',
    'initialData',
    'onSite',
    'roundIndex',
    'tonsByRoundLength',
  ],

  setup() {
    return {
      addCircle,
      arrowBackCircleOutline,
      closeCircle,
    };
  },

  data() {
    return {
      tons: null,
      reference: null,
      source_worksite: null,
      target_worksite: null,
      materials: [],
    };
  },

  mounted() {
    let materials = this.defaultMaterials;

    if (this.initialData) {
      this.tons = this.initialData.value;
      this.reference = this.initialData.reference;
      this.source_worksite = this.initialData.source_worksite;
      this.target_worksite = this.initialData.target_worksite;

      if (this.initialData.materials && this.initialData.materials.length) {
        materials = this.initialData.materials;
      }
    }

    this.materials = JSON.parse(JSON.stringify(materials || []));
  },

  methods: {
    async addMaterial() {
      if (!this.canAddMaterial) {
        return;
      }

      const modal = await modalController.create({
        component: PopoverAddMaterial,
        componentProps: {
          closePopover: newMaterial => {
            // user can simply close modal, so ensure we actually have a new material to add
            if (newMaterial) {
              this.materials.push(newMaterial);
            }

            modal.dismiss();
          },
          suggestions: this.materialSuggestions,
        },
      });

      await modal.present();
      await modal.onDidDismiss();
    },

    deleteMaterial(materialToRemove) {
      if (this.isReadOnly) return;

      const index = this.materials.indexOf(materialToRemove);

      if (index > -1) {
        this.materials.splice(index, 1);
      }
    },

    validate() {
      if (this.disableValidation) return;

      const tons = this.tons?.toString().trim().replace(',', '.');
      const value = Number(tons);

      if (tons && (!Number.isFinite(value) || value < 0)) {
        sendErrorToast('Saisissez un poids supérieur ou égal à 0.');
        return;
      }

      const data = {
        value: this.tons,
        reference: this.reference,
        source_worksite: this.source_worksite,
        target_worksite: this.target_worksite,
      };

      // if materials changed, add it to data
      if (
        JSON.stringify(this.materials || []) !==
        JSON.stringify(this.defaultMaterials || [])
      ) {
        data.materials = this.materials;
      }

      this.closePopover(data);
    },

    async confirmDelete() {
      if (this.isReadOnly || !this.initialData) {
        return;
      }

      const alert = await alertController.create({
        header: 'Confirmation',
        message: 'Voulez-vous vraiment supprimer ce tour ?',
        buttons: [
          {
            text: 'Annuler',
            role: 'cancel',
          },
          {
            text: 'Supprimer',
            role: 'destructive',
            handler: () => this.closePopover({ delete: true }),
          },
        ],
      });

      await alert.present();
    },
  },

  computed: {
    roundName() {
      const index =
        this.roundIndex === null
          ? this.tonsByRoundLength
          : Number(this.roundIndex);

      // if it's the only round, label it as "Tour 1 ou Total"
      if (index === 0 && this.tonsByRoundLength <= 1) {
        return 'Tour 1 ou Total';
      }

      // new round
      return `Tour ${index + 1}`;
    },

    isReadOnly() {
      return this.oldRentalCouponSending || this.disabled;
    },

    canAddMaterial() {
      return !this.isReadOnly && (!this.materials || this.materials.length < 1); // One max
    },

    allFieldsEmpty() {
      return (
        !this.tons &&
        !this.reference &&
        !this.source_worksite &&
        !this.target_worksite &&
        (!this.materials || !this.materials.length)
      );
    },

    disableValidation() {
      return (
        this.isReadOnly ||
        this.allFieldsEmpty ||
        (this.materials && this.materials.length > 1) // more than one
      );
    },
  },
};
</script>

<style scoped lang="scss">
ion-footer {
  &.md {
    padding: 0 16px;
  }

  ion-button {
    text-transform: none;
  }
}

.subtitle {
  font-size: 0.8em;
  font-weight: normal;
  color: var(--ion-color-medium);
}

/* don't know why but color="danger" is not working on the card, so use custom css */
.card-danger {
  background: var(--ion-color-danger) !important;
  color: var(--ion-color-danger-contrast, white) !important;

  ion-card-title,
  ion-card-content {
    color: var(--ion-color-danger-contrast, white) !important;
  }
}
</style>
